import { baseApi } from '@klp/core/api';
import { generateApiRoute } from '@klp/core/api/helpers';
import { brukerApiName } from '@klp/user/config';
import type { IPortfolio } from '../classes/IPortfolio';
import { Portfolio } from '../classes/Portfolio';
import { PortfolioResponse } from '../classes/PortfolioResponse';
import type { IRegion } from '../classes/IRegion';
import { Region } from '../classes/Region';
import { RegionResponse } from '../classes/RegionResponse';

const portfolioApiName = 'portfolio';

// The lists rarely change, so they are kept for a while after the last
// selector has unmounted, and every page reuses the same response
const listCacheSeconds = 900;

const portfolioRoutes = {
    portfolioList: () => generateApiRoute([brukerApiName]),
    regionList: () => generateApiRoute([portfolioApiName, 'regioner']),
};

const portfolioApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        // The backend returns every user, and the list is filtered in the
        // selectors
        getPortfolioList: build.query<PortfolioResponse, void>({
            query: () => ({
                url: portfolioRoutes.portfolioList(),
                method: 'GET',
            }),
            transformResponse: (response: Array<IPortfolio>) => {
                const portfolioList = response.map((portfolio: IPortfolio) =>
                    Portfolio.parseObject(portfolio),
                );
                return new PortfolioResponse({ portfolioList });
            },
            providesTags: ['PortfolioList'],
            keepUnusedDataFor: listCacheSeconds,
        }),
        getRegionList: build.query<RegionResponse, void>({
            query: () => ({
                url: portfolioRoutes.regionList(),
                method: 'GET',
            }),
            transformResponse: (response: Array<IRegion['name']>) => {
                const regionList = response.map((region) =>
                    Region.parseObject({ name: region }),
                );
                return new RegionResponse({ regionList });
            },
            providesTags: ['RegionList'],
            keepUnusedDataFor: listCacheSeconds,
        }),
    }),
});

export const {
    useGetPortfolioListQuery,
    useLazyGetPortfolioListQuery,
    useGetRegionListQuery,
    useLazyGetRegionListQuery,
} = portfolioApi;
